import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { MarkdownEditor } from 'components/blocks/markdown-editor';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useGetToolTutorial, useSaveTutorial } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IToolDetails } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useConfigStore } from 'state/store/config/useConfigStore';
import {
  extractTutorialContent,
  getToolTutorial,
  patchToolTutorialInCache,
} from './tool-detail-utils';

export function ToolTutorialGuide({ tool }: { tool: IToolDetails }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = useIsOwner(tool);
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const { data: tutorialData, isLoading: isTutorialLoading } = useGetToolTutorial(
    tool.id,
    projectKey
  );
  const { mutateAsync: saveTutorial, isPending } = useSaveTutorial();

  const defaultContent = useMemo(
    () =>
      `# ${t('TUTORIAL_GUIDE') || 'Tutorial Guide'}\n\n${t('NO_TUTORIAL_GUIDE_ADDED_YET') || 'No tutorial guide has been added for this tool yet.'
      }`,
    [t]
  );

  const resolvedTutorial = useMemo(() => {
    const fromTutorialEndpoint = extractTutorialContent(tutorialData);
    if (fromTutorialEndpoint) return fromTutorialEndpoint;

    return getToolTutorial(tool);
  }, [tutorialData, tool]);

  const [content, setContent] = useState(() => resolvedTutorial || defaultContent);

  useEffect(() => {
    setContent(resolvedTutorial || defaultContent);
  }, [defaultContent, resolvedTutorial, tool.id]);

  const handleSave = async () => {
    if (!canEdit || !projectKey) return;

    try {
      const response = await saveTutorial({
        toolId: tool.id,
        projectKey,
        payload: {
          tutorial: content,
          Tutorial: content,
          id: tool.id,
          tool_id: tool.id,
          is_open_tool: tool.is_open_tool,
        },
      });

      if (response.is_success === false) {
        throw new Error(
          response.message || response.detail || t('FAILED_TO_SAVE_TUTORIAL_GUIDE') || 'Failed to save tutorial guide'
        );
      }

      patchToolTutorialInCache(queryClient, tool.id, projectKey, content);

      await queryClient.invalidateQueries({ queryKey: ['tools', 'tutorial', tool.id, projectKey] });
      await queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] });

      toast({
        variant: 'success',
        title: t('TUTORIAL_SAVED') || 'Tutorial saved',
        description:
          t('TUTORIAL_SAVED_SUCCESSFULLY'),
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('SAVE_FAILED') || 'Save failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_SAVE_TUTORIAL_GUIDE') || 'Failed to save tutorial guide.',
      });
    }
  };

  if (isTutorialLoading) {
    return (
      <div className="flex h-[calc(100vh-280px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="flex h-[calc(100vh-280px)] flex-col">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>{t('EDIT') || 'Edit'}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{t('MARKDOWN') || 'Markdown'}</Badge>
            {canEdit ? (
              <Button size="sm" onClick={handleSave} disabled={isPending}>
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isPending ? t('SAVING') || 'Saving...' : t('SAVE') || 'Save'}
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <MarkdownEditor
            value={content}
            onChange={setContent}
            showCharacterCount
            className="h-full flex-1"
            contentClassName="h-full min-h-full flex-1"
            placeholder={t('WRITE_HERE') || 'Write here...'}
            showToolbar={canEdit}
            showModeToggle={false}
            defaultMode="edit"
            disabled={!canEdit}
          />
        </CardContent>
      </Card>

      <Card className="flex h-[calc(100vh-280px)] flex-col">
        <CardHeader>
          <CardTitle>{t('PREVIEW') || 'Preview'}</CardTitle>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-y-auto">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <MarkdownRenderer content={content} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
